// « Keep where I was » (owner spec 2026-10-01).
//   • The last admin page is remembered (localStorage). When the INSTALLED app
//     is opened at exactly /admin — the home-screen icon's start_url — with no
//     deep link, and the last visit was under 12 h ago, it goes back there.
//     A notification tap opens its own URL (an order, a conversation), which is
//     never /admin bare, so it is never overridden; neither is any link that
//     carries a path, a query or a hash, nor anything after the first screen.
//   • Each page's scroll position is kept for the session (sessionStorage) and
//     put back when the person comes back to it with Back / swipe-back.
// Storage can be blocked (private mode, full quota): every access is guarded.
import { isAdminPath } from '@/lib/pwa/detect';

export const LAST_PATH_KEY = 'storiz_adm_last_path';
export const LAST_PATH_MAX_AGE_MS = 12 * 60 * 60 * 1000;
export const SCROLL_KEY = 'storiz_adm_scroll';
const SCROLL_MAX_PATHS = 50;

function pathnameOf(path: string): string {
  return path.split(/[?#]/)[0];
}

export function recordLastPath(storage: Storage, path: string, now: number): void {
  if (!isAdminPath(pathnameOf(path))) return;
  try { storage.setItem(LAST_PATH_KEY, JSON.stringify({ path, at: now })); } catch { /* blocked */ }
}

export interface LaunchInfo {
  pathname: string;
  search: string;
  hash: string;
  /** Still the very first screen of this page load (React Router key 'default'). */
  isInitial: boolean;
  /** Opened as the installed app (lib/pwa/detect.isStandalone). */
  standalone: boolean;
}

export function pathToRestore(storage: Storage, now: number, launch: LaunchInfo): string | null {
  if (!launch.standalone || !launch.isInitial) return null;
  if (launch.pathname !== '/admin' || launch.search || launch.hash) return null;
  let raw: string | null;
  try { raw = storage.getItem(LAST_PATH_KEY); } catch { return null; }
  if (!raw) return null;
  let saved: { path?: unknown; at?: unknown };
  try { saved = JSON.parse(raw); } catch { return null; }
  if (typeof saved?.path !== 'string' || typeof saved.at !== 'number') return null;
  const age = now - saved.at;
  if (age < 0 || age > LAST_PATH_MAX_AGE_MS) return null;
  const path = saved.path;
  // Same-origin admin paths only: never « //host/… » or « https://… ».
  if (!path.startsWith('/') || path.startsWith('//')) return null;
  const pn = pathnameOf(path);
  if (!isAdminPath(pn) || path === '/admin') return null;
  return path;
}

function readMap(storage: Storage): Record<string, number> {
  try {
    const raw = storage.getItem(SCROLL_KEY);
    const v = raw ? JSON.parse(raw) : {};
    return v && typeof v === 'object' ? v : {};
  } catch { return {}; }
}

export function saveScroll(storage: Storage, key: string, y: number): void {
  const map = readMap(storage);
  delete map[key]; // re-insert last = most recent
  map[key] = Math.max(0, Math.round(y));
  const keys = Object.keys(map);
  for (const k of keys.slice(0, Math.max(0, keys.length - SCROLL_MAX_PATHS))) delete map[k];
  try { storage.setItem(SCROLL_KEY, JSON.stringify(map)); } catch { /* blocked */ }
}

export function readScroll(storage: Storage, key: string): number | null {
  const v = readMap(storage)[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}
